import {
  ITEM_HOTBAR_SLOT_COUNT,
  setItemHotbarSlot,
  type HomeEconomy,
} from "@/home/economy";
import { itemHotbarUseCooldownRemainingMs } from "@/home/item-hotbar-use";
import { tierFromStashItemId } from "@/home/health-potions";
import { tierFromManaStashItemId } from "@/home/mana-potions";
import {
  getStashSlotById,
  isStashItemId,
  STASH_ITEM_DRAG_MIME,
  type StashItemId,
} from "@/home/home-stash-modal";

export { setItemHotbarSlot };

/** Extra locked slots shown in the play HUD (economy still uses {@link ITEM_HOTBAR_SLOT_COUNT}). */
export const ITEM_HOTBAR_UI_EXTRA_LOCKED = 2;

export function renderItemHotbarHtml(prefix: "home" | "tower"): string {
  const uiSlotCount = ITEM_HOTBAR_SLOT_COUNT + ITEM_HOTBAR_UI_EXTRA_LOCKED;
  const slots = Array.from({ length: uiSlotCount }, (_, index) => {
    if (index >= ITEM_HOTBAR_SLOT_COUNT) {
      return `<button
        type="button"
        class="home-item-hotbar-slot home-item-hotbar-slot--locked"
        disabled
        aria-label="ช่องล็อก"
        title="ปลดล็อกในอนาคต"
      >
        <span class="home-item-hotbar-slot-lock" aria-hidden="true">🔒</span>
      </button>`;
    }
    return `<button
        type="button"
        class="home-item-hotbar-slot"
        id="${prefix}-item-hotbar-${index}"
        data-hotbar-index="${index}"
        data-hotbar-item=""
        aria-label="ช่องไอเทม ${index + 1}"
      >
        <span class="home-item-hotbar-slot-index" aria-hidden="true">${index + 1}</span>
        <img class="home-item-hotbar-slot-img" src="" alt="" width="32" height="32" hidden />
        <span class="home-potion-slot-qty home-item-hotbar-slot-qty" hidden>0</span>
      </button>`;
  }).join("");
  return `<div class="home-combat-hotbar-row home-combat-hotbar-row--items">
      <div class="home-potion-slot-frame home-item-hotbar" aria-label="ช่องไอเทมด่วน">${slots}</div>
    </div>`;
}

export function syncItemHotbar(
  root: ParentNode,
  economy: HomeEconomy,
  prefix: "home" | "tower",
  options?: { disabled?: boolean; itemUseCooldown?: boolean },
): void {
  const blockedAll = Boolean(options?.disabled);
  const onItemCooldown = Boolean(options?.itemUseCooldown);
  for (let index = 0; index < ITEM_HOTBAR_SLOT_COUNT; index += 1) {
    const btn = root.querySelector<HTMLButtonElement>(`#${prefix}-item-hotbar-${index}`);
    if (!btn) continue;
    const itemId = economy.itemHotbarSlots[index];
    const img = btn.querySelector<HTMLImageElement>(".home-item-hotbar-slot-img");
    const qtyEl = btn.querySelector<HTMLElement>(".home-item-hotbar-slot-qty");
    if (!itemId) {
      btn.dataset.hotbarItem = "";
      btn.classList.add("home-item-hotbar-slot--empty");
      btn.classList.remove("home-item-hotbar-slot--blocked");
      if (img) {
        img.hidden = true;
        img.removeAttribute("src");
      }
      if (qtyEl) qtyEl.hidden = true;
      btn.title = "ลากไอเทมจากคลังมาวาง";
      btn.setAttribute("aria-disabled", "false");
      btn.draggable = false;
      continue;
    }
    const stash = getStashSlotById(economy, itemId);
    const count = stash?.count ?? 0;
    const empty = count <= 0;
    const blocked = blockedAll || empty || onItemCooldown;
    btn.dataset.hotbarItem = itemId;
    btn.classList.toggle("home-item-hotbar-slot--empty", false);
    btn.classList.toggle("home-item-hotbar-slot--blocked", blocked);
    if (img && stash) {
      img.src = stash.imageDataUrl;
      img.alt = "";
      img.hidden = false;
    }
    if (qtyEl) {
      qtyEl.textContent = count.toLocaleString("en-US");
      qtyEl.hidden = false;
    }
    btn.title = stash
      ? empty
        ? `${stash.name} · หมด · ซื้อที่ร้าน`
        : `${stash.name} · ${stash.hint} · คงเหลือ ${count} · ลากออกนอกแถบเพื่อเอาออก`
      : itemId;
    btn.setAttribute("aria-disabled", blocked ? "true" : "false");
    btn.draggable = true;
  }
}

export function stashItemIdFromHotbarClick(target: EventTarget | null): {
  index: number;
  itemId: StashItemId;
} | null {
  const btn = (target as HTMLElement | null)?.closest<HTMLButtonElement>("[data-hotbar-index]");
  if (!btn || btn.getAttribute("aria-disabled") === "true") return null;
  const index = Number(btn.dataset.hotbarIndex);
  const itemId = btn.dataset.hotbarItem;
  if (!Number.isFinite(index) || !itemId || !isStashItemId(itemId)) return null;
  return { index, itemId };
}

function readDraggedStashItem(event: DragEvent): StashItemId | null {
  const dt = event.dataTransfer;
  if (!dt) return null;
  const custom = dt.getData(STASH_ITEM_DRAG_MIME);
  if (custom && isStashItemId(custom)) return custom;
  const plain = dt.getData("text/plain");
  if (plain && isStashItemId(plain)) return plain;
  return null;
}

let suppressStashOpenClickUntil = 0;
let suppressHotbarUseClickUntil = 0;
let hotbarDragFromIndex: number | null = null;
let hotbarDragDroppedOnSlot = false;

function setHotbarDragChrome(active: boolean): void {
  document.body.classList.toggle("home-item-hotbar-drag", active);
}

function clearHotbarDropTargets(root: ParentNode): void {
  root.querySelectorAll(".home-item-hotbar-slot--drop-target").forEach((el) => {
    el.classList.remove("home-item-hotbar-slot--drop-target");
  });
}

function hotbarSlotAtPoint(clientX: number, clientY: number): HTMLButtonElement | null {
  const stack =
    typeof document.elementsFromPoint === "function"
      ? document.elementsFromPoint(clientX, clientY)
      : [document.elementFromPoint(clientX, clientY)].filter(Boolean);
  for (const hit of stack) {
    const slot = (hit as HTMLElement).closest?.<HTMLButtonElement>("[data-hotbar-index]");
    if (!slot || slot.disabled || slot.classList.contains("home-item-hotbar-slot--locked")) {
      continue;
    }
    const index = Number(slot.dataset.hotbarIndex);
    if (!Number.isFinite(index) || index < 0 || index >= ITEM_HOTBAR_SLOT_COUNT) continue;
    return slot;
  }
  return null;
}

/** Skip stash slot click-to-detail right after a drag ended. */
export function shouldSuppressStashOpenClick(): boolean {
  return Date.now() < suppressStashOpenClickUntil;
}

export function shouldSuppressHotbarUseClick(): boolean {
  return Date.now() < suppressHotbarUseClickUntil;
}

export type ItemHotbarBindings = {
  prefix: "home" | "tower";
  getEconomy: () => HomeEconomy;
  onAssign: (index: number, itemId: StashItemId | null) => void;
  onUseItem: (itemId: StashItemId) => void;
  onSlotAutoChange?: (index: number, enabled: boolean) => void;
  isUseBlocked?: () => boolean;
};

export function bindItemHotbar(root: HTMLElement, bindings: ItemHotbarBindings): () => void {
  const assignToSlot = (slot: HTMLButtonElement, event: DragEvent): void => {
    const itemId = readDraggedStashItem(event);
    if (!itemId) return;
    const index = Number(slot.dataset.hotbarIndex);
    if (!Number.isFinite(index) || index < 0 || index >= ITEM_HOTBAR_SLOT_COUNT) return;
    const fromIndex = hotbarDragFromIndex;
    bindings.onAssign(index, itemId);
    hotbarDragDroppedOnSlot = true;
    if (fromIndex !== null && fromIndex !== index) {
      bindings.onAssign(fromIndex, null);
    }
  };

  const onDragStart = (event: DragEvent) => {
    const stashEl = (event.target as HTMLElement).closest<HTMLElement>("[data-stash-drag-item]");
    if (stashEl) {
      const id = stashEl.dataset.stashDragItem;
      if (!id || !isStashItemId(id)) return;
      const dt = event.dataTransfer;
      if (!dt) return;
      dt.setData(STASH_ITEM_DRAG_MIME, id);
      dt.setData("text/plain", id);
      dt.effectAllowed = "copy";
      stashEl.classList.add("home-stash-slot--dragging");
      setHotbarDragChrome(true);
      return;
    }
    const slotBtn = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-hotbar-index]");
    if (slotBtn?.dataset.hotbarItem && isStashItemId(slotBtn.dataset.hotbarItem)) {
      const fromIndex = Number(slotBtn.dataset.hotbarIndex);
      if (!Number.isFinite(fromIndex)) return;
      const dt = event.dataTransfer;
      if (!dt) return;
      hotbarDragFromIndex = fromIndex;
      hotbarDragDroppedOnSlot = false;
      dt.setData(STASH_ITEM_DRAG_MIME, slotBtn.dataset.hotbarItem);
      dt.setData("text/plain", slotBtn.dataset.hotbarItem);
      dt.setData("application/x-home-hotbar-source-index", String(fromIndex));
      dt.effectAllowed = "move";
      slotBtn.classList.add("home-item-hotbar-slot--dragging");
      setHotbarDragChrome(true);
    }
  };

  const onDragEnd = (event: DragEvent) => {
    const target = event.target as HTMLElement;
    const fromHotbar = target.closest("[data-hotbar-index]");
    target.closest(".home-item-hotbar-slot")?.classList.remove("home-item-hotbar-slot--dragging");
    target.closest(".home-stash-slot")?.classList.remove("home-stash-slot--dragging");
    if (target.closest("[data-stash-drag-item]")) {
      suppressStashOpenClickUntil = Date.now() + 400;
    }
    if (fromHotbar && hotbarDragFromIndex !== null) {
      suppressHotbarUseClickUntil = Date.now() + 400;
      if (!hotbarDragDroppedOnSlot) {
        bindings.onAssign(hotbarDragFromIndex, null);
      }
    }
    hotbarDragFromIndex = null;
    hotbarDragDroppedOnSlot = false;
    setHotbarDragChrome(false);
    clearHotbarDropTargets(root);
  };

  const onDocumentDragOver = (event: DragEvent) => {
    if (!document.body.classList.contains("home-item-hotbar-drag")) return;
    const slot = hotbarSlotAtPoint(event.clientX, event.clientY);
    if (!slot) {
      clearHotbarDropTargets(root);
      return;
    }
    event.preventDefault();
    const dt = event.dataTransfer;
    if (dt) {
      dt.dropEffect = hotbarDragFromIndex !== null ? "move" : "copy";
    }
    clearHotbarDropTargets(root);
    slot.classList.add("home-item-hotbar-slot--drop-target");
  };

  const onDocumentDrop = (event: DragEvent) => {
    if (!document.body.classList.contains("home-item-hotbar-drag")) return;
    const slot = hotbarSlotAtPoint(event.clientX, event.clientY);
    if (!slot) return;
    event.preventDefault();
    event.stopPropagation();
    slot.classList.remove("home-item-hotbar-slot--drop-target");
    assignToSlot(slot, event);
  };

  const onHotbarDragOver = (event: DragEvent) => {
    if (!document.body.classList.contains("home-item-hotbar-drag")) return;
    onDocumentDragOver(event);
  };

  const onDrop = (event: DragEvent) => {
    onDocumentDrop(event);
  };

  const onClick = (event: Event) => {
    if (shouldSuppressHotbarUseClick()) return;
    const parsed = stashItemIdFromHotbarClick(event.target);
    if (!parsed) return;
    if (bindings.isUseBlocked?.()) return;
    bindings.onUseItem(parsed.itemId);
  };

  const onChange = (event: Event) => {
    const input = (event.target as HTMLElement).closest<HTMLInputElement>(
      "[data-item-hotbar-auto-all], [data-item-hotbar-auto]",
    );
    if (!input) return;
    if (input.dataset.itemHotbarAutoAll !== undefined) {
      const enabled = input.checked;
      for (let index = 0; index < ITEM_HOTBAR_SLOT_COUNT; index += 1) {
        bindings.onSlotAutoChange?.(index, enabled);
      }
      return;
    }
    if (!input.dataset.itemHotbarAuto) return;
    const index = Number(input.dataset.itemHotbarAuto);
    if (!Number.isFinite(index)) return;
    bindings.onSlotAutoChange?.(index, input.checked);
  };

  const onContextMenu = (event: Event) => {
    const slot = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-hotbar-index]");
    if (!slot) return;
    event.preventDefault();
    const index = Number(slot.dataset.hotbarIndex);
    if (!Number.isFinite(index)) return;
    bindings.onAssign(index, null);
  };

  const hotbarRoot = root.querySelector<HTMLElement>(".home-combat-hotbar") ?? root;

  root.addEventListener("dragstart", onDragStart, true);
  root.addEventListener("dragend", onDragEnd, true);
  document.addEventListener("dragover", onDocumentDragOver, true);
  document.addEventListener("drop", onDocumentDrop, true);
  hotbarRoot.addEventListener("dragover", onHotbarDragOver);
  hotbarRoot.addEventListener("drop", onDrop);
  hotbarRoot.addEventListener("click", onClick);
  hotbarRoot.addEventListener("contextmenu", onContextMenu);
  hotbarRoot.addEventListener("change", onChange);

  return () => {
    root.removeEventListener("dragstart", onDragStart, true);
    root.removeEventListener("dragend", onDragEnd, true);
    document.removeEventListener("dragover", onDocumentDragOver, true);
    document.removeEventListener("drop", onDocumentDrop, true);
    hotbarRoot.removeEventListener("dragover", onHotbarDragOver);
    hotbarRoot.removeEventListener("drop", onDrop);
    hotbarRoot.removeEventListener("click", onClick);
    hotbarRoot.removeEventListener("contextmenu", onContextMenu);
    hotbarRoot.removeEventListener("change", onChange);
    setHotbarDragChrome(false);
    clearHotbarDropTargets(root);
  };
}

export function syncItemHotbarCooldownState(root: ParentNode, _prefix: "home" | "tower"): void {
  const onCd = itemHotbarUseCooldownRemainingMs() > 0;
  const frame = root.querySelector(".home-item-hotbar");
  frame?.classList.toggle("home-item-hotbar--cooldown", onCd);
}

export function hotbarUseBlockedReason(itemId: StashItemId): string | null {
  if (tierFromStashItemId(itemId)) return null;
  if (tierFromManaStashItemId(itemId)) return null;
  if (itemId === "revive_card") return "บัตรชุบใช้ในหอคอย";
  if (itemId === "skill_cooldown_card") return "บัตรลดคูลดาวน์ใช้ที่หน้าสกิล";
  if (itemId === "skill_ap_card") return "บัตร AP ใช้ที่สกิลบัฟในคลังสกิล";
  if (itemId === "skill_buff_duration_card") return "บัตรเวลาบัฟ ใช้ที่คลังสกิลบัฟ";
  if (itemId === "skill_aoe_range_card") return "บัตรระยะสกิลหมู่ ใช้ที่คลังสกิล";
  return "ใช้ไอเทมนี้ไม่ได้";
}
